/**
 * Capability-aware WRITE gate (lot A1, 2026-09-25).
 *
 * THE PROBLEM it fixes: a `users.role === 'read-only'` account whose BAND role is
 * `owner`/`editor` (a real production case) got the full editor UI, while
 * `server/index.js` refuses every non-GET `/api` mutation for that account
 * (`403 read-only account`). The user edited a field and read a red
 * « Not saved » banner on an action that could never succeed.
 *
 * THE RULE (single, capability-aware) — mirrors the server:
 *
 *   canWrite(bandId, capability?) =
 *        global_role === 'superadmin'
 *     OR (band role ∈ {owner, editor}  AND  users.role !== 'read-only')
 *     OR (capability given AND granted by the capability matrix: can(bandId, cap))
 *
 * So a global read-only account keeps writes ONLY through the 7 delegable
 * capabilities (`layout.rider|daysheet|tourbook|presets`,
 * `finance.expenses|fees|budget`) — which are mounted AHEAD of the server's
 * global read-only guard — and everything else is frozen for it.
 *
 * NOT covered here on purpose:
 *  - SELF-SERVICE (`/api/me`, `/api/account`, the user's own Team card /
 *    documents / password / 2FA / sessions): the server mounts those ahead of the
 *    read-only guard, so a read-only member keeps them. Never gate them.
 *  - The SHOWCASE (demo) account: `isShowcaseMode()` already cuts every write at
 *    `fetchJSON`, and `blockShowcaseWrites` 403s its self-service routes. That
 *    freeze has PRIORITY over every grant → `canWrite` is always false for the
 *    demo. The global <ShowcaseBanner/> explains it, so no local note is added.
 *  - The server stays the authority: this gate only removes impossible clicks.
 */

import { useCallback, useMemo } from 'react';
import type { BandRole } from '../api';
import { can, type Permission } from './permissions';
import { isShowcaseMode } from './saveStatus';
import { useAuth } from '../context/AuthContext';
import { useProfileContext } from '../hooks/useProfileContext';

/** Why the write controls are frozen (null = they are not). */
export type WriteGateReason = 'global-read-only' | null;

export interface WriteGateInput {
  /** `users.role` — the legacy account role: 'admin' | 'manager' | 'read-only'. */
  legacyRole?: string | null;
  /** `users.global_role === 'superadmin'` — implicit full write everywhere. */
  isSuperAdmin?: boolean;
  /** Read-only showcase (demo) account — frozen whatever the grants. */
  isShowcase?: boolean;
  /** The caller's role on the ACTIVE band ('owner'|'editor'|'viewer'). */
  bandRole?: BandRole | null;
  /** The EFFECTIVE capability matrix on the band (`BandProfile.permissions`). */
  permissions?: string[] | null;
  /** LEGACY compat flags of `can()` (used when no matrix is present). */
  financeAccess?: boolean | number | null;
  layoutAccess?: boolean | number | null;
}

/**
 * True for a real account carrying the global `read-only` ceiling. A superadmin
 * is never affected (the platform role wins), and the showcase (demo) is
 * deliberately excluded: its freeze comes from the demo flag, not from the
 * ceiling, and the app-wide <ShowcaseBanner/> already explains it — reporting
 * both would double up the message.
 */
export function isGlobalReadOnlyAccount(input: WriteGateInput | null | undefined): boolean {
  if (!input) return false;
  if (input.isSuperAdmin || input.isShowcase) return false;
  return input.legacyRole === 'read-only';
}

/**
 * THE rule — `capability` is the optional delegable capability the control
 * belongs to (`layout.rider`, `finance.fees`, …). Same exact function is what
 * `useWriteGate().canWrite` (no capability) and `.canWriteCap(cap)` call.
 */
export function canWriteBand(
  input: WriteGateInput | null | undefined,
  capability?: Permission,
): boolean {
  if (!input) return false;
  // The demo account never writes, even with a grant — see the file header.
  if (input.isShowcase) return false;
  if (input.isSuperAdmin) return true;
  const bandEditor = input.bandRole === 'owner' || input.bandRole === 'editor';
  if (bandEditor && input.legacyRole !== 'read-only') return true;
  if (capability) {
    return can(
      {
        permissions: input.permissions,
        finance_access: input.financeAccess ?? null,
        layout_access: input.layoutAccess ?? null,
      },
      capability,
    );
  }
  return false;
}

/** `'global-read-only'` when the account carries the global read-only ceiling. */
export function writeGateReason(input: WriteGateInput | null | undefined): WriteGateReason {
  return isGlobalReadOnlyAccount(input) ? 'global-read-only' : null;
}

/**
 * Predicate for the routes the server mounts AHEAD of the read-only guard that
 * accept a **band editor regardless of the global ceiling** — today only the
 * guestlist (`canWriteGuests`, `server/routes/tour-extra.js:49`: superadmin OR
 * band role ≥ editor). A read-only account that IS editor on the band may write
 * those, so the UI must NOT freeze them: freezing a legal action is the same
 * class of bug as letting an impossible one through.
 *
 * Do not use it for anything else: every other family is behind the guard (or
 * needs an explicit capability). The showcase stays frozen (its banner explains
 * why, and `blockShowcaseWrites` 403s it server-side).
 */
export function canWriteAsBandEditor(input: WriteGateInput | null | undefined): boolean {
  if (!input) return false;
  if (input.isShowcase) return false;
  if (input.isSuperAdmin) return true;
  return input.bandRole === 'owner' || input.bandRole === 'editor';
}

export interface WriteGate {
  /** May the user write on this band AT ALL (no capability carve-out)? */
  canWrite: boolean;
  /** May the user write the surface behind that exact capability? */
  canWriteCap: (capability: Permission) => boolean;
  /**
   * May the user write the surfaces the server exempts for a band EDITOR even
   * when the account carries the global read-only ceiling (today: the
   * guestlist). Never use it as a general "can write" escape hatch.
   */
  canWriteEditor: boolean;
  /** Why the controls are frozen — drives the ONE inline note. */
  readOnlyReason: WriteGateReason;
}

/**
 * Thin hook wrapper: builds the input from the auth session + the active band
 * profile. `bandId` defaults to the active band; pass the entity's own band when
 * the component knows it better (a show seeds it from `form.band_id`).
 */
export function useWriteGate(bandId?: string | null): WriteGate {
  const { user } = useAuth();
  const { profiles, activeProfileId } = useProfileContext();
  const id = bandId || activeProfileId;
  const profile = id ? profiles.find((p) => p.id === id) ?? null : null;
  const isSuperAdmin = user?.global_role === 'superadmin';

  const input = useMemo<WriteGateInput>(
    () => ({
      legacyRole: user?.role ?? null,
      isSuperAdmin,
      isShowcase: !!user?.is_showcase || isShowcaseMode(),
      // A superadmin owns every band implicitly (same convention as App.tsx).
      bandRole: isSuperAdmin ? 'owner' : profile?.my_role ?? null,
      permissions: profile?.permissions ?? null,
      financeAccess: profile?.finance_access ?? null,
      layoutAccess: profile?.layout_access ?? null,
    }),
    [
      user?.role,
      user?.is_showcase,
      isSuperAdmin,
      profile?.my_role,
      profile?.permissions,
      profile?.finance_access,
      profile?.layout_access,
    ],
  );

  const canWrite = canWriteBand(input);
  const canWriteCap = useCallback(
    (capability: Permission) => canWriteBand(input, capability),
    [input],
  );

  return {
    canWrite,
    canWriteCap,
    canWriteEditor: canWriteAsBandEditor(input),
    readOnlyReason: writeGateReason(input),
  };
}